import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import {
  useGetTrainingCourseQuery,
  useUpdateTrainingCourseMutation,
  useSetTrainingCourseStatusMutation,
  useAddTrainingQuizModuleMutation,
  useAddTrainingVideoModuleMutation,
  useUpdateTrainingModuleMutation,
  useDeleteTrainingModuleMutation,
  useReorderTrainingModulesMutation,
  useGetTrainingVideoUrlMutation,
  useListTrainingSandboxUsersQuery,
  useAddTrainingSandboxUserMutation,
  useRemoveTrainingSandboxUserMutation,
  useLoadCompanyDirectoryQuery,
  useSubmitTrainingReviewMutation,
  useWithdrawTrainingReviewMutation,
  useApproveTrainingReviewMutation,
  useRejectTrainingReviewMutation,
} from '../app/api';
import { selectAdminUuid } from '../features/auth/authSlice';
import { useIsMobile, getRoleJSON } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const STATUS_STYLE = {
  draft:           { bg: 'rgba(88,88,91,0.15)',   color: brand.charcoal,  label: 'Draft' },
  sandbox:         { bg: 'rgba(208,155,44,0.15)', color: brand.goldenrod, label: 'Sandbox' },
  pending_review:  { bg: 'rgba(176,111,0,0.18)',  color: '#b06f00',       label: 'Pending review' },
  published:       { bg: 'rgba(26,127,78,0.15)',  color: brand.green,     label: 'Published' },
  archived:        { bg: 'rgba(176,0,32,0.10)',   color: brand.red,       label: 'Archived' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem', borderRadius: 999,
    }}>{s.label}</span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
Field.propTypes = { label: PropTypes.string, children: PropTypes.node };

function initialsOf(first, last) {
  return ((first || '').charAt(0) + (last || '').charAt(0)).toUpperCase() || '?';
}

SandboxAllowlist.propTypes = { courseId: PropTypes.number.isRequired };
function SandboxAllowlist({ courseId }) {
  const { data: allowlist = [], isLoading: isLoadingList } = useListTrainingSandboxUsersQuery({ id: courseId });
  const { data: directory = [] } = useLoadCompanyDirectoryQuery();
  const [addUser, { isLoading: isAdding }] = useAddTrainingSandboxUserMutation();
  const [removeUser] = useRemoveTrainingSandboxUserMutation();
  const [query, setQuery] = useState('');
  const [err, setErr] = useState(null);

  const allowedEmails = new Set(
    allowlist
      .map(r => (r.final_email || r.username || '').toLowerCase())
      .filter(Boolean)
  );

  const suggestions = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return directory
      .filter(d => {
        const email = (d.final_email || '').toLowerCase();
        if (!email || allowedEmails.has(email)) return false;
        const hay = `${d.first_name || ''} ${d.last_name || ''} ${email} ${d.title || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  })();

  const handleAdd = async (emp) => {
    setErr(null);
    try {
      await addUser({ id: courseId, email: emp.final_email }).unwrap();
      setQuery('');
    } catch (e) {
      setErr(e?.data?.detail || 'Could not add user.');
    }
  };

  const handleRemove = async (uuid) => {
    setErr(null);
    try {
      await removeUser({ id: courseId, user_uuid: uuid }).unwrap();
    } catch (e) {
      setErr(e?.data?.detail || 'Could not remove user.');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
        <i className="bi bi-flask" style={{ color: brand.goldenrod }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 700, margin: 0, fontSize: '1rem' }}>Sandbox allowlist</h5>
      </div>
      <p style={{ color: brand.charcoal, fontSize: '0.825rem', margin: '0 0 0.85rem' }}>
        Only these users see this course while it&apos;s in sandbox. The list is preserved if you move the course back to draft, so you don&apos;t lose your pilot group.
      </p>

      {err && (
        <div style={{ background: 'rgba(176,0,32,0.08)', color: brand.red, border: `1px solid rgba(176,0,32,0.25)`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.825rem', marginBottom: '0.75rem' }}>{err}</div>
      )}

      <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search employees by name or email…"
          style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '0.45rem 0.7rem', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none' }}
        />
        {suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 6, boxShadow: '0 4px 16px rgba(10,41,71,0.12)', marginTop: 4, zIndex: 50, maxHeight: 260, overflowY: 'auto' }}>
            {suggestions.map(d => (
              <button
                key={d.id}
                type="button"
                onClick={() => handleAdd(d)}
                disabled={isAdding}
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', background: 'white', border: 'none', borderBottom: `1px solid ${brand.softGray}`, padding: '0.5rem 0.7rem', textAlign: 'left', cursor: 'pointer' }}
              >
                {d.photo_url ? (
                  <img src={d.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: brand.tealBlue, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                    {initialsOf(d.first_name, d.last_name)}
                  </div>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.85rem' }}>{d.first_name} {d.last_name}</div>
                  <div style={{ color: brand.charcoal, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.final_email}</div>
                </div>
                <i className="bi bi-plus-circle" style={{ color: brand.tealBlue }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoadingList ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>Loading…</div>
      ) : allowlist.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', fontStyle: 'italic' }}>
          No users yet. The course is invisible to everyone until you add at least one.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {allowlist.map(u => {
            const display = (u.first_name || u.last_name)
              ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
              : (u.final_email || u.username || u.user_uuid);
            return (
              <li key={u.user_uuid} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.5rem', border: `1px solid ${brand.softGray}`, borderRadius: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: brand.tealBlue, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                  {initialsOf(u.first_name, u.last_name)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.85rem' }}>{display}</div>
                  {u.final_email && (
                    <div style={{ color: brand.charcoal, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.final_email}</div>
                  )}
                </div>
                <button type="button" onClick={() => handleRemove(u.user_uuid)} title="Remove" style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.red, borderRadius: 6, padding: '0.25rem 0.5rem', fontSize: '0.78rem', cursor: 'pointer' }}>
                  <i className="bi bi-x-lg" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// A locally-generated question id so the row in the editor has a stable key
// from the first keystroke. The backend regenerates ids it doesn't trust,
// but assigning one here means the radio name attribute and the
// `answers[q.id]` lookup on the learner side never see `undefined`.
function newQuestionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function emptyQuestion() {
  return { id: newQuestionId(), text: '', choices: ['', ''], correct_idx: 0, explanation: '' };
}

QuizBuilder.propTypes = {
  initial: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};
function QuizBuilder({ initial, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(initial?.title || 'Knowledge Check');
  const [questions, setQuestions] = useState(() => {
    if (initial?.payload?.questions?.length) {
      return initial.payload.questions.map(q => ({
        id: q.id,
        text: q.text,
        choices: [...q.choices],
        correct_idx: q.correct_idx,
        explanation: q.explanation || '',
      }));
    }
    return [emptyQuestion()];
  });
  const [err, setErr] = useState(null);

  const updateQuestion = (idx, patch) => {
    setQuestions(qs => qs.map((q, i) => i === idx ? { ...q, ...patch } : q));
  };

  const updateChoice = (qi, ci, value) => {
    setQuestions(qs => qs.map((q, i) => i === qi
      ? { ...q, choices: q.choices.map((c, j) => j === ci ? value : c) }
      : q
    ));
  };

  const addChoice = (qi) => {
    setQuestions(qs => qs.map((q, i) => i === qi
      ? { ...q, choices: [...q.choices, ''] }
      : q
    ));
  };

  const removeChoice = (qi, ci) => {
    setQuestions(qs => qs.map((q, i) => {
      if (i !== qi) return q;
      const next = q.choices.filter((_, j) => j !== ci);
      // Keep correct_idx in range; if the deleted choice was correct, fall back to 0.
      const correct_idx = ci === q.correct_idx ? 0
                        : ci < q.correct_idx ? q.correct_idx - 1
                        : q.correct_idx;
      return { ...q, choices: next, correct_idx };
    }));
  };

  const handleSave = () => {
    setErr(null);
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.text.trim()) { setErr(`Question ${i + 1} is missing text.`); return; }
      if (q.choices.length < 2) { setErr(`Question ${i + 1} needs at least 2 choices.`); return; }
      if (q.choices.some(c => !c.trim())) { setErr(`Question ${i + 1} has an empty choice.`); return; }
    }
    onSave({
      title: title.trim() || 'Knowledge Check',
      questions: questions.map(q => ({
        id: q.id,
        text: q.text.trim(),
        choices: q.choices.map(c => c.trim()),
        correct_idx: q.correct_idx,
        explanation: (q.explanation || '').trim() || null,
      })),
    });
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, padding: '1.25rem', maxWidth: 720, width: '100%', boxShadow: '0 10px 40px rgba(10,41,71,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
      <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>{initial ? 'Edit quiz' : 'Add quiz module'}</h5>

      <Field label="Module title">
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem' }}
        />
      </Field>

      {questions.map((q, qi) => (
        <div key={qi} style={{ border: `1px solid ${brand.softGray}`, borderRadius: 10, padding: '0.85rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem' }}>Question {qi + 1}</span>
            {questions.length > 1 && (
              <button
                type="button"
                onClick={() => setQuestions(qs => qs.filter((_, i) => i !== qi))}
                style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: brand.red, fontSize: '0.8rem', cursor: 'pointer' }}
              >
                <i className="bi bi-trash" /> Remove
              </button>
            )}
          </div>

          <textarea
            value={q.text}
            onChange={e => updateQuestion(qi, { text: e.target.value })}
            rows={2}
            placeholder="Question text"
            style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.9rem', resize: 'vertical', marginBottom: '0.5rem' }}
          />

          {q.choices.map((c, ci) => (
            <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
              <input
                type="radio"
                name={`q-${qi}-correct`}
                checked={q.correct_idx === ci}
                onChange={() => updateQuestion(qi, { correct_idx: ci })}
                title="Mark as correct"
              />
              <input
                type="text"
                value={c}
                onChange={e => updateChoice(qi, ci, e.target.value)}
                placeholder={`Choice ${ci + 1}`}
                style={{ flex: 1, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.875rem' }}
              />
              {q.choices.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeChoice(qi, ci)}
                  style={{ background: 'transparent', border: 0, color: brand.charcoal, cursor: 'pointer' }}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
          ))}

          {q.choices.length < 6 && (
            <button
              type="button"
              onClick={() => addChoice(qi)}
              style={{ background: 'transparent', border: 0, color: brand.tealBlue, fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.25rem' }}
            >
              <i className="bi bi-plus-lg" /> Add choice
            </button>
          )}

          <div style={{ marginTop: '0.65rem' }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 4 }}>
              Correct-answer explanation (optional)
            </label>
            <textarea
              value={q.explanation || ''}
              onChange={e => updateQuestion(qi, { explanation: e.target.value })}
              rows={2}
              placeholder="Shown only when the learner gets this question right — explain why their answer is correct."
              style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.85rem', resize: 'vertical' }}
            />
            <p style={{ fontSize: '0.72rem', color: brand.charcoal, margin: '0.25rem 0 0' }}>
              Wrong answers always get the same prompt to rewatch and retry — no need to write a wrong-answer note.
            </p>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => setQuestions(qs => [...qs, emptyQuestion()])}
        style={{ background: 'transparent', color: brand.tealBlue, border: `1px dashed ${brand.tealBlue}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontWeight: 600, fontSize: '0.85rem', width: '100%', marginBottom: '0.75rem', cursor: 'pointer' }}
      >
        <i className="bi bi-plus-lg me-1" /> Add another question
      </button>

      {err && <div style={{ color: brand.red, fontSize: '0.85rem', marginBottom: '0.75rem' }}>{err}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={saving} style={{ background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600 }}>Cancel</button>
        <button type="button" onClick={handleSave} disabled={saving} style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700 }}>
          {saving ? 'Saving…' : 'Save quiz'}
        </button>
      </div>
    </div>
  );
}

VideoPreviewModal.propTypes = {
  moduleId: PropTypes.number.isRequired,
  filename: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
function VideoPreviewModal({ moduleId, filename, onClose }) {
  const [getVideoUrl] = useGetTrainingVideoUrlMutation();
  const [url, setUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getVideoUrl({ id: moduleId }).unwrap().then(res => {
      if (!cancelled) setUrl(res.url);
    }).catch(err => {
      if (!cancelled) setLoadErr(err?.data?.detail || 'Could not load video.');
    });
    return () => { cancelled = true; };
  }, [moduleId, getVideoUrl]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: '1rem', maxWidth: 800, width: '100%', boxShadow: '0 10px 40px rgba(10,41,71,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, fontSize: '1rem' }}>{filename || 'Video preview'}</h5>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: brand.charcoal, fontSize: '1.25rem', cursor: 'pointer' }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {loadErr && <div style={{ color: brand.red, padding: '1rem' }}>{loadErr}</div>}
        {!url && !loadErr && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        )}
        {url && (
          <video
            src={url}
            controls
            style={{ width: '100%', maxHeight: '70vh', background: '#000', borderRadius: 8 }}
          />
        )}
      </div>
    </div>
  );
}

export default function TrainingCourseEditor() {
  const { courseId } = useParams();
  const isMobile = useIsMobile();
  const id = Number(courseId);

  const { data: course, isLoading } = useGetTrainingCourseQuery({ id }, { skip: !id });
  const [updateCourse, { isLoading: isUpdating }] = useUpdateTrainingCourseMutation();
  const [setStatus, { isLoading: isChangingStatus }] = useSetTrainingCourseStatusMutation();
  const [addQuiz, { isLoading: isAddingQuiz }] = useAddTrainingQuizModuleMutation();
  const [addVideo, { isLoading: isAddingVideo }] = useAddTrainingVideoModuleMutation();
  const [updateModule] = useUpdateTrainingModuleMutation();
  const [deleteModule] = useDeleteTrainingModuleMutation();
  const [reorderModules] = useReorderTrainingModulesMutation();
  const [submitReview, { isLoading: isSubmittingReview }] = useSubmitTrainingReviewMutation();
  const [withdrawReview, { isLoading: isWithdrawing }] = useWithdrawTrainingReviewMutation();
  const [approveReview, { isLoading: isApproving }] = useApproveTrainingReviewMutation();
  const [rejectReview, { isLoading: isRejecting }] = useRejectTrainingReviewMutation();

  // The editor is gated to Compliance at the route level (Root.jsx). The
  // additional Admin role unlocks Approve/Reject for pending submissions.
  const currentUserUuid = useSelector(selectAdminUuid);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (currentUserUuid) {
      getRoleJSON(currentUserUuid).then(rj => { if (!cancelled) setIsAdmin(Boolean(rj?.isAdmin)); });
    }
    return () => { cancelled = true; };
  }, [currentUserUuid]);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editThreshold, setEditThreshold] = useState(80);
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [error, setError] = useState(null);

  const [quizModal, setQuizModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', module }
  const [videoPreview, setVideoPreview] = useState(null); // { id, filename }
  // Inline title-edit state for video modules. { id, value, saving } when active.
  const [titleEdit, setTitleEdit] = useState(null);
  const videoInputRef = useRef(null);

  // Seed the local edit state when the course loads/changes.
  useEffect(() => {
    if (course) {
      setEditTitle(course.title || '');
      setEditDescription(course.description || '');
      setEditThreshold(course.pass_threshold || 80);
      setDetailsDirty(false);
    }
  }, [course]);

  const handleSaveDetails = async () => {
    setError(null);
    try {
      await updateCourse({
        id,
        body: {
          title: editTitle,
          description: editDescription || null,
          pass_threshold: Number(editThreshold) || 80,
        },
      }).unwrap();
      setDetailsDirty(false);
    } catch (err) {
      setError(err?.data?.detail || 'Could not save course details.');
    }
  };

  const handleStatus = async (newStatus) => {
    setError(null);
    try {
      await setStatus({ id, status: newStatus }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not change status.');
    }
  };

  const handleSubmitReview = async () => {
    setError(null);
    try {
      await submitReview({ id }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not submit for review.');
    }
  };

  const handleWithdrawReview = async () => {
    setError(null);
    try {
      await withdrawReview({ id }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not withdraw the submission.');
    }
  };

  const handleApprove = async () => {
    setError(null);
    try {
      await approveReview({ id }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not approve.');
    }
  };

  const handleReject = async () => {
    setError(null);
    try {
      await rejectReview({ id, reason: rejectReason.trim() || null }).unwrap();
      setRejectModalOpen(false);
      setRejectReason('');
    } catch (err) {
      setError(err?.data?.detail || 'Could not reject.');
    }
  };

  const handleAddQuiz = async (payload) => {
    setError(null);
    try {
      await addQuiz({
        courseId: id,
        body: {
          title: payload.title,
          questions: payload.questions,
        },
      }).unwrap();
      setQuizModal(null);
    } catch (err) {
      setError(err?.data?.detail || 'Could not add quiz.');
    }
  };

  const handleSaveModuleTitle = async () => {
    if (!titleEdit) return;
    const value = titleEdit.value.trim();
    if (!value) {
      setError('Title cannot be empty.');
      return;
    }
    setError(null);
    setTitleEdit({ ...titleEdit, saving: true });
    try {
      await updateModule({ id: titleEdit.id, courseId: id, body: { title: value } }).unwrap();
      setTitleEdit(null);
    } catch (err) {
      setError(err?.data?.detail || 'Could not save title.');
      setTitleEdit({ ...titleEdit, saving: false });
    }
  };

  const handleEditQuiz = async (payload) => {
    setError(null);
    const mod = quizModal?.module;
    if (!mod) return;
    try {
      await updateModule({
        id: mod.id,
        courseId: id,
        body: {
          title: payload.title,
          payload: {
            questions: payload.questions.map(q => ({
              id: q.id,
              text: q.text,
              choices: q.choices,
              correct_idx: q.correct_idx,
              explanation: q.explanation || null,
            })),
          },
        },
      }).unwrap();
      setQuizModal(null);
    } catch (err) {
      setError(err?.data?.detail || 'Could not save quiz.');
    }
  };

  const handleVideoUpload = async (file) => {
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await addVideo({ courseId: id, body: fd }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Video upload failed.');
    }
  };

  const handleDeleteModule = async (mod) => {
    if (!window.confirm(`Delete module "${mod.title || mod.kind}"?`)) return;
    try {
      await deleteModule({ id: mod.id, courseId: id }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not delete module.');
    }
  };

  const handleMove = async (modIdx, dir) => {
    if (!course?.modules) return;
    const next = [...course.modules];
    const swap = modIdx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[modIdx], next[swap]] = [next[swap], next[modIdx]];
    try {
      await reorderModules({ courseId: id, ordered_ids: next.map(m => m.id) }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not reorder.');
    }
  };

  if (isLoading || !course) {
    return (
      <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: brand.tealBlue }} />
      </div>
    );
  }

  const modules = course.modules || [];

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem' }}>

      <Link to="/compliance/training" style={{ color: brand.tealBlue, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
        <i className="bi bi-chevron-left" /> All courses
      </Link>

      {course.status === 'sandbox' && (
        <div style={{
          background: 'repeating-linear-gradient(135deg, rgba(208,155,44,0.18) 0 14px, rgba(208,155,44,0.30) 14px 28px)',
          border: `2px solid ${brand.goldenrod}`,
          borderRadius: 12,
          padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.1rem',
          marginTop: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <i className="bi bi-flask" style={{ color: brand.goldenrod, fontSize: '1.5rem', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Sandbox mode
            </div>
            <div style={{ color: brand.deepBlue, fontSize: '0.825rem', marginTop: 2 }}>
              This course is hidden from everyone except the users you add to the allowlist below. Publish when you&apos;re ready to make it visible to all employees.
            </div>
          </div>
        </div>
      )}

      {course.status === 'pending_review' && (
        <div style={{
          background: 'rgba(176,111,0,0.10)',
          border: `2px solid #b06f00`,
          borderRadius: 12,
          padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.1rem',
          marginTop: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <i className="bi bi-hourglass-split" style={{ color: '#b06f00', fontSize: '1.5rem', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Pending admin approval
            </div>
            <div style={{ color: brand.deepBlue, fontSize: '0.825rem', marginTop: 2 }}>
              Submitted {course.requested_at ? new Date(course.requested_at).toLocaleString() : ''}. The course will go live to all employees once an admin approves. Any sandbox testers keep their access in the meantime. Editing the course will pull it out of review (back to draft, or back to sandbox if there&apos;s an allowlist) and require a fresh submission.
            </div>
          </div>
        </div>
      )}

      {(course.status === 'draft' || course.status === 'sandbox') && course.rejection_reason && (
        <div style={{
          background: 'rgba(176,0,32,0.08)',
          border: `1px solid rgba(176,0,32,0.3)`,
          borderRadius: 12,
          padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.1rem',
          marginTop: '0.75rem',
          display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <i className="bi bi-x-octagon" style={{ color: brand.red, fontSize: '1.35rem', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: brand.red, fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Last submission rejected
            </div>
            <div style={{ color: brand.deepBlue, fontSize: '0.825rem', marginTop: 2, whiteSpace: 'pre-wrap' }}>
              {course.rejection_reason}
            </div>
            {course.reviewed_at && (
              <div style={{ color: brand.charcoal, fontSize: '0.75rem', marginTop: 4 }}>
                {new Date(course.reviewed_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header banner */}
      <div style={{
        background: brand.deepBlue,
        borderRadius: '12px',
        padding: isMobile ? '1.25rem 1rem' : '1.5rem 1.75rem',
        marginTop: '0.75rem',
        marginBottom: isMobile ? '1rem' : '1.5rem',
        position: 'relative',
        overflow: 'hidden',
        border: course.status === 'sandbox' ? `2px solid ${brand.goldenrod}` : 'none',
      }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: 4 }}>
              <StatusBadge status={course.status} />
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                Pass {course.pass_threshold}% · {modules.length} module{modules.length === 1 ? '' : 's'}
              </span>
            </div>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: isMobile ? '1.15rem' : '1.5rem', lineHeight: 1.2 }}>{course.title}</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
            {course.status === 'draft' && (
              <>
                <button type="button" onClick={handleSubmitReview} disabled={isSubmittingReview || modules.length === 0} title={modules.length === 0 ? 'Add at least one module before submitting' : 'Send to an admin for approval'} style={{ background: brand.goldenrod, color: brand.deepBlue, border: 0, borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <i className="bi bi-send me-1" /> Submit for review
                </button>
                <button type="button" onClick={() => handleStatus('sandbox')} disabled={isChangingStatus || modules.length === 0} title={modules.length === 0 ? 'Add at least one module to sandbox' : 'Visible only to users you add to the allowlist'} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <i className="bi bi-flask me-1" /> Sandbox
                </button>
              </>
            )}
            {course.status === 'sandbox' && (
              <>
                <button type="button" onClick={handleSubmitReview} disabled={isSubmittingReview || modules.length === 0} style={{ background: brand.goldenrod, color: brand.deepBlue, border: 0, borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <i className="bi bi-send me-1" /> Submit for review
                </button>
                <button type="button" onClick={() => handleStatus('draft')} disabled={isChangingStatus} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <i className="bi bi-arrow-counterclockwise me-1" /> Back to draft
                </button>
              </>
            )}
            {course.status === 'pending_review' && (
              <>
                {isAdmin && (
                  <>
                    <button type="button" onClick={handleApprove} disabled={isApproving} style={{ background: brand.goldenrod, color: brand.deepBlue, border: 0, borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                      <i className="bi bi-check2-circle me-1" /> Approve
                    </button>
                    <button type="button" onClick={() => { setRejectReason(''); setRejectModalOpen(true); }} disabled={isRejecting} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                      <i className="bi bi-x-circle me-1" /> Reject
                    </button>
                  </>
                )}
                <button type="button" onClick={handleWithdrawReview} disabled={isWithdrawing} title="Cancel this submission and return to draft" style={{ background: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <i className="bi bi-arrow-counterclockwise me-1" /> Withdraw
                </button>
              </>
            )}
            {course.status === 'published' && (
              <button type="button" onClick={() => handleStatus('draft')} disabled={isChangingStatus} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                <i className="bi bi-arrow-counterclockwise me-1" /> Unpublish
              </button>
            )}
            {course.status !== 'archived' && (
              <button type="button" onClick={() => handleStatus('archived')} disabled={isChangingStatus} style={{ background: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                Archive
              </button>
            )}
            {course.status === 'archived' && (
              <button type="button" onClick={() => handleStatus('draft')} disabled={isChangingStatus} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                Reopen
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(176,0,32,0.08)', color: brand.red, border: `1px solid rgba(176,0,32,0.25)`, borderRadius: 8, padding: '0.6rem 0.85rem', fontSize: '0.875rem', marginBottom: '1rem' }}>{error}</div>
      )}

      {course.status === 'sandbox' && <SandboxAllowlist courseId={id} />}

      {/* Course details */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: isMobile ? '1rem' : '1.25rem', marginBottom: '1.25rem' }}>
        <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1rem' }}>Course details</h5>
        <Field label="Title">
          <input
            type="text"
            value={editTitle}
            onChange={e => { setEditTitle(e.target.value); setDetailsDirty(true); }}
            style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem' }}
          />
        </Field>
        <Field label="Description">
          <textarea
            value={editDescription}
            onChange={e => { setEditDescription(e.target.value); setDetailsDirty(true); }}
            rows={3}
            style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem', resize: 'vertical' }}
          />
        </Field>
        <div style={{ maxWidth: 220 }}>
          <Field label="Pass threshold (%)">
            <input
              type="number"
              min={0}
              max={100}
              value={editThreshold}
              onChange={e => { setEditThreshold(e.target.value); setDetailsDirty(true); }}
              style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem' }}
            />
          </Field>
        </div>
        {detailsDirty && (
          <button type="button" onClick={handleSaveDetails} disabled={isUpdating} style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.85rem' }}>
            {isUpdating ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>

      {/* Modules */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem' }}>
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Modules</h5>
        </div>
        {modules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: brand.charcoal }}>
            <i className="bi bi-collection-play" style={{ fontSize: '2.25rem', color: brand.softGray, display: 'block', marginBottom: '0.5rem' }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No modules yet.</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>Add a video or quiz below.</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {modules.map((mod, idx) => {
              const isVideo = mod.kind === 'video';
              const questions = mod.payload?.questions || [];
              return (
                <li key={mod.id} style={{ padding: isMobile ? '0.75rem 1rem' : '0.85rem 1.25rem', borderBottom: idx < modules.length - 1 ? `1px solid ${brand.softGray}` : 'none', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button type="button" onClick={() => handleMove(idx, -1)} disabled={idx === 0} style={{ background: 'transparent', border: 0, color: idx === 0 ? brand.softGray : brand.charcoal, padding: 0, cursor: idx === 0 ? 'default' : 'pointer' }} aria-label="Move up">
                      <i className="bi bi-chevron-up" />
                    </button>
                    <button type="button" onClick={() => handleMove(idx, 1)} disabled={idx === modules.length - 1} style={{ background: 'transparent', border: 0, color: idx === modules.length - 1 ? brand.softGray : brand.charcoal, padding: 0, cursor: idx === modules.length - 1 ? 'default' : 'pointer' }} aria-label="Move down">
                      <i className="bi bi-chevron-down" />
                    </button>
                  </div>
                  <div style={{ background: isVideo ? 'rgba(0,105,166,0.1)' : 'rgba(208,155,44,0.15)', color: isVideo ? brand.tealBlue : brand.goldenrod, width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`bi ${isVideo ? 'bi-play-btn' : 'bi-patch-question'}`} style={{ fontSize: '1.1rem' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {titleEdit && titleEdit.id === mod.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input
                          type="text"
                          value={titleEdit.value}
                          autoFocus
                          disabled={titleEdit.saving}
                          onChange={e => setTitleEdit({ ...titleEdit, value: e.target.value })}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); handleSaveModuleTitle(); }
                            if (e.key === 'Escape') { e.preventDefault(); setTitleEdit(null); }
                          }}
                          style={{ flex: 1, minWidth: 0, border: `1px solid ${brand.tealBlue}`, borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.9rem', color: brand.deepBlue, outline: 'none' }}
                        />
                        <button type="button" onClick={handleSaveModuleTitle} disabled={titleEdit.saving} style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                          {titleEdit.saving ? '…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setTitleEdit(null)} disabled={titleEdit.saving} style={{ background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod.title || (isVideo ? 'Video' : 'Quiz')}</div>
                        <div style={{ fontSize: '0.78rem', color: brand.charcoal }}>
                          {isVideo
                            ? (mod.payload?.filename || 'video file')
                            : `${questions.length} question${questions.length === 1 ? '' : 's'}`}
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    {isVideo && (!titleEdit || titleEdit.id !== mod.id) && (
                      <>
                        <button type="button" onClick={() => setTitleEdit({ id: mod.id, value: mod.title || '', saving: false })} title="Rename" style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.tealBlue, borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                          <i className="bi bi-pencil" /> Rename
                        </button>
                        <button type="button" onClick={() => setVideoPreview({ id: mod.id, filename: mod.payload?.filename })} style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.tealBlue, borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                          <i className="bi bi-play" /> Preview
                        </button>
                      </>
                    )}
                    {!isVideo && (!titleEdit || titleEdit.id !== mod.id) && (
                      <button type="button" onClick={() => setQuizModal({ mode: 'edit', module: mod })} style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.tealBlue, borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                        <i className="bi bi-pencil" /> Edit
                      </button>
                    )}
                    {(!titleEdit || titleEdit.id !== mod.id) && (
                      <button type="button" onClick={() => handleDeleteModule(mod)} style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.red, borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                        <i className="bi bi-trash" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Add module bar */}
        <div style={{ background: brand.softGray, padding: '0.85rem 1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleVideoUpload(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => videoInputRef.current?.click()}
            disabled={isAddingVideo}
            style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.5rem 0.85rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            <i className="bi bi-upload me-1" /> {isAddingVideo ? 'Uploading…' : 'Add video'}
          </button>
          <button
            type="button"
            onClick={() => setQuizModal({ mode: 'create' })}
            disabled={isAddingQuiz}
            style={{ background: brand.goldenrod, color: brand.deepBlue, border: 0, borderRadius: 8, padding: '0.5rem 0.85rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            <i className="bi bi-patch-question me-1" /> Add quiz
          </button>
          <span style={{ color: brand.charcoal, fontSize: '0.75rem', alignSelf: 'center' }}>
            Videos up to 500 MB · MP4/MOV/M4V/WebM
          </span>
        </div>
      </div>

      {/* Modals */}
      {rejectModalOpen && (
        <div onClick={() => setRejectModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 500, background: 'white', borderRadius: 12, padding: '1.25rem 1.4rem', boxShadow: '0 12px 40px rgba(10,41,71,0.3)' }}>
            <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>
              <i className="bi bi-x-octagon me-2" style={{ color: brand.red }} />
              Reject this submission?
            </h5>
            <p style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: '0.85rem' }}>
              The course will return to draft and the reason will be shown to Compliance.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.55rem 0.75rem', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
              <button type="button" onClick={() => setRejectModalOpen(false)} disabled={isRejecting} style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: brand.charcoal, borderRadius: 8, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={handleReject} disabled={isRejecting} style={{ background: brand.red, border: 0, color: 'white', borderRadius: 8, padding: '0.45rem 0.95rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                {isRejecting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {quizModal && (
        <div onClick={() => setQuizModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 720 }}>
            <QuizBuilder
              initial={quizModal.mode === 'edit' ? quizModal.module : null}
              onSave={quizModal.mode === 'edit' ? handleEditQuiz : handleAddQuiz}
              onCancel={() => setQuizModal(null)}
              saving={isAddingQuiz}
            />
          </div>
        </div>
      )}

      {videoPreview && (
        <VideoPreviewModal
          moduleId={videoPreview.id}
          filename={videoPreview.filename}
          onClose={() => setVideoPreview(null)}
        />
      )}
    </div>
  );
}
